import LanguageStringResolver from "../../fields/language/LanguageStringResolver";
import React, { useState, useEffect } from "react";
import { SSelect } from '@avalara/skylab-sdk/react';
import {renderInputField} from "../../common/RenderInputField";
import {ManualDocumentDocumentType} from "../../../utils/enums";

const IntrastatForm = props => {
    const {country} = props.selectedValues;
    const countryDispatch = props.selectedValues.documentType === ManualDocumentDocumentType.SALES ?  country : "";
    const countryArrival = props.selectedValues.documentType === ManualDocumentDocumentType.PURCHASE ?  country : "";
    
    const getInitialValues = () => ({
        reportingType: props.selectedValues.documentType === ManualDocumentDocumentType.SALES ? 1 : 2,
        transactionType: "",
        intrastatCode: "",
        vatNumberSupplier: "",
        vatNumberCustomer: "",
        unit1: "kg",
        quantity1: 0,
        unit2: "",
        quantity2: 0,
        commercialValue: 0,
        statisticalValue: 0,
        modeOfTransport: "",
        deliveryCondition: "",
        countryDispatch: countryDispatch,
        countryArrival: countryArrival,
        regionDispatch: "",
        regionArrival: "",
        harbourDispatch: "",
        harbourArrival: "",
        countryOfOrigin: "",
        statisticalProcedure: "",
    });

    useEffect(() => {
        if(props.selectedValues.documentType === ManualDocumentDocumentType.SALES) {
            setFormValues(prev => ({
                ...prev,
                countryDispatch: props.selectedValues?.country,
                countryArrival: "",
            }));
        } else if (props.selectedValues.documentType === ManualDocumentDocumentType.PURCHASE) {
            setFormValues(prev => ({
                ...prev,
                countryArrival: props.selectedValues?.country,
                countryDispatch: ""
            }));
        }
    }, [props.selectedValues]);
    
    const [formValues, setFormValues] = useState(getInitialValues);
    const [showError, setShowError] = useState(false);
    const requiredFields = ["intrastatCode", "vatNumberSupplier", "vatNumberCustomer",  "modeOfTransport", "deliveryCondition", "countryDispatch", "countryArrival", "countryOfOrigin"]

    const handleInputChange = e => {
        const { name, value } = e.target;
        setFormValues(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSelectInputChange = e => {
        const { selectInputId, item } = e;
        setFormValues(prev => ({
            ...prev,
            [selectInputId]: item.value,
        }));
    };
    
    const handleDeSelectChange = e => {
        const { selectInputId } = e;
        setFormValues(prev => ({
            ...prev,
            [selectInputId]: "",
        }));
    }

    const IsValidForm = () => {
        // check values for required fields
        return requiredFields.filter(o => formValues[o].toString().trim().length === 0).length === 0;
    }

    const onFormSubmit = () => {
        if(!IsValidForm()){
            setShowError(true);
        } else {
            // Handle empty input having empty value
            const payload = {
                ...formValues,
                reportingType: props.selectedValues.documentType === ManualDocumentDocumentType.SALES ? 1 : 2,
                quantity1: formValues['quantity1'].toString().trim().length > 0 ? formValues['quantity1'] : 0,
                quantity2: formValues['quantity2'].toString().trim().length > 0 ? formValues['quantity2'] : 0,
                commercialValue: formValues['commercialValue'].toString().trim().length > 0 ? formValues['commercialValue'] : 0,
                statisticalValue: formValues['statisticalValue'].toString().trim().length > 0 ? formValues['statisticalValue'] : 0
            }
               
            props.appendFormData(payload);
            onReset();
        }
    }

    const onReset = () => {
        setShowError(false);
        resetSSelect();
        setFormValues(getInitialValues());
    }

    const resetSSelect = () => {
        // There is no way to de-select skylab drop-down even after changing state, hence handling using javascript
        const closeIconAllElement = document.querySelectorAll('button[ref="deselect-all-button"]');
        if(closeIconAllElement.length) {
            for (const ele in closeIconAllElement) {
                if(closeIconAllElement[ele].click) {
                    closeIconAllElement[ele].click()
                }
            }
        }
    }

    const mapToAddKeyValues = (data, selectedValue, addValuePrefix = false) => {
        return data.map(obj => ({
            label: addValuePrefix ? `${obj.value}-${obj.name}` : obj.name,
            value: `${obj.value}`,
            selected: selectedValue === `${obj.value}`
        }));
    }

    const getCountryDropDownOptions = (selectedFieldValue) => {
        return props.countryList.map(country => ({
            value: country.countryCode,
            label: `${country.countryName} (${country.countryCode})`,
            selected: selectedFieldValue === country.countryCode,
        }));
    };

    const validationErrorSection = () => {
        const validationRules = [
            { key: 'intrastatCode', errorMsg: 'intrastat_manual_document_intrastat_code_err_msg' },
            { key: 'vatNumberSupplier', errorMsg: 'intrastat_manual_document_vat_number_supplier_err_msg' },
            { key: 'vatNumberCustomer', errorMsg: 'intrastat_manual_document_vat_number_customer_err_msg' },
            { key: 'modeOfTransport', errorMsg: 'intrastat_manual_document_mode_of_transport_err_msg' },
            { key: 'deliveryCondition', errorMsg: 'intrastat_manual_document_delivery_condition_err_msg' },
            { key: 'countryArrival', errorMsg: 'intrastat_manual_document_country_arrival_err_msg' },
            { key: 'countryDispatch', errorMsg: 'intrastat_manual_document_country_dispatch_err_msg' },
            { key: 'countryOfOrigin', errorMsg: 'intrastat_manual_document_country_of_origin_err_msg' }
        ];

        return (
            <>
                {showError && validationRules.map(({ key, errorMsg }) => {
                    const value = formValues[key]?.toString().trim();
                    return value.length === 0 && (
                        <s-alert key={key} status="error" data-testid={`${key}_err_msg`} nodismiss={true}>
                            <LanguageStringResolver string={errorMsg} />
                        </s-alert>
                    );
                })}
            </>
        );
    };

    return (
        <>
            <h3><LanguageStringResolver string='manual_document_intrastat_form_title'/></h3>
            <div>
                <label className='mb15'>
                    <LanguageStringResolver string='manual_document_form_description'/>
                </label>
            </div>
            <s-container class="mb50 mt20" data-testid="intrastat-form">
                {validationErrorSection()}
                <form>
                    {renderInputField("intrastatCode", "intrastat_manual_document_intrastat_code", formValues, handleInputChange, "text", true)}
                    {renderInputField("vatNumberSupplier", "intrastat_manual_document_vat_number_supplier", formValues, handleInputChange, "text", true)}
                    {renderInputField("vatNumberCustomer", "intrastat_manual_document_vat_number_customer", formValues, handleInputChange, "text", true)}

                    <s-row>
                        <s-col span="auto">
                            <label htmlFor="quantity1" data-testid="quantity1">
                                <LanguageStringResolver string="intrastat_manual_document_quantity1"/>
                            </label>
                            <input
                                id="quantity1"
                                data-testid="quantity1-input"
                                type="number"
                                name="quantity1"
                                value={formValues.quantity1}
                                onChange={handleInputChange}
                            />
                        </s-col>
                        <s-col span="auto" data-testid="unit1">
                            <label htmlFor="unit1">
                                <LanguageStringResolver string="intrastat_manual_document_unit1"/>
                            </label>
                            <input
                                id="unit1"
                                data-testid="unit1-input"
                                type="text"
                                name="unit1"
                                value={formValues.unit1}
                                placeholder="kg"
                                onChange={handleInputChange}
                            />
                        </s-col>
                    </s-row>
                    <s-row>
                        <s-col span="auto" data-testid="quantity2">
                            <label htmlFor="quantity2">
                                <LanguageStringResolver string="intrastat_manual_document_quantity2"/>
                            </label>
                            <input data-testid="quantity2-input" id="quantity2" value={formValues.quantity2} type="number" name="quantity2" onChange={handleInputChange}/>
                        </s-col>
                        <s-col span="auto" data-testid="unit2">
                            <label htmlFor="unit2">
                                <LanguageStringResolver string="intrastat_manual_document_unit2"/>
                            </label>
                            <input
                                id="unit2"
                                data-testid="unit2-input"
                                type="text"
                                name="unit2"
                                value={formValues.unit2}
                                onChange={handleInputChange}
                            />
                        </s-col>
                    </s-row>

                    {renderInputField("commercialValue", "intrastat_manual_document_commercial_value", formValues, handleInputChange, "number",)}
                    {renderInputField("statisticalValue", "intrastat_manual_document_statistical_value", formValues, handleInputChange, "number",)}

                    <s-row>
                        <s-col span="auto" data-testid="modeOfTransport">
                            <label id="modeOfTransportLabel" htmlFor="modeOfTransport" className="required">
                                <LanguageStringResolver string="intrastat_manual_document_mode_of_transport"/>
                            </label>
                            <SSelect
                                name="modeOfTransport"
                                aria-labelledby="modeOfTransportLabel"
                                data-testid="modeOfTransport-select"
                                inputId="modeOfTransport"
                                optionsList={mapToAddKeyValues(props.modeOfTransport, formValues.modeOfTransport, true)}
                                noSearch={false}
                                selectionOptional={true}
                                replacealloptions={true}
                                value={formValues.modeOfTransport}
                                onS-select={e => handleSelectInputChange(e.detail)}
                                onS-deselect={e => handleDeSelectChange(e.detail)}
                            />
                        </s-col>
                        <s-col span="auto" data-testid="deliveryCondition">
                            <label id="deliveryConditionLabel" htmlFor="deliveryCondition" className="required">
                                <LanguageStringResolver string="intrastat_manual_document_delivery_condition"/>
                            </label>
                            <SSelect
                                name="deliveryCondition"
                                aria-labelledby="deliveryConditionLabel"
                                data-testid="deliveryConditions-select"
                                inputId="deliveryCondition"
                                optionsList={mapToAddKeyValues(props.deliveryConditions, formValues.deliveryCondition, false)}
                                noSearch={false}
                                selectionOptional={true}
                                replacealloptions={true}
                                value={formValues.deliveryCondition}
                                onS-select={e => handleSelectInputChange(e.detail)}
                                onS-deselect={e => handleDeSelectChange(e.detail)}
                            />
                        </s-col>
                    </s-row>
                    <s-row>
                        <s-col span="auto" data-testid="countryDispatch">
                            <label htmlFor="countryDispatch" className="required">
                                <LanguageStringResolver string="intrastat_manual_document_country_dispatch"/>
                            </label>
                            <SSelect
                                name="countryDispatch"
                                data-testid="countryDispatch-select"
                                inputId="countryDispatch"
                                optionsList={getCountryDropDownOptions(formValues.countryDispatch)}
                                noSearch={false}
                                selectionOptional={true}
                                replacealloptions={true}
                                value={formValues.countryDispatch}
                                onS-select={e => handleSelectInputChange(e.detail)}
                                onS-deselect={e => handleDeSelectChange(e.detail)}
                                disabled={props.selectedValues.documentType === ManualDocumentDocumentType.SALES}
                            />
                        </s-col>
                        <s-col span="auto" data-testid="countryArrival">
                            <label id="countryArrivalLabel" htmlFor="countryArrival" className="required">
                                <LanguageStringResolver string="intrastat_manual_document_country_arrival"/>
                            </label>
                            <SSelect
                                name="countryArrival"
                                aria-labelledby="countryArrivalLabel"
                                data-testid="countryArrival-select"
                                inputId="countryArrival"
                                optionsList={getCountryDropDownOptions(formValues.countryArrival)}
                                noSearch={false}
                                selectionOptional={true}
                                replacealloptions={true}
                                value={formValues.countryArrival}
                                onS-select={e => handleSelectInputChange(e.detail)}
                                onS-deselect={e => handleDeSelectChange(e.detail)}
                                disabled={props.selectedValues.documentType === ManualDocumentDocumentType.PURCHASE}
                            />
                        </s-col>
                    </s-row>

                    {renderInputField("regionDispatch", "intrastat_manual_document_region_dispatch", formValues, handleInputChange)}
                    {renderInputField("regionArrival", "intrastat_manual_document_region_arrival", formValues, handleInputChange)}
                    {renderInputField("harbourDispatch", "intrastat_manual_document_harbour_dispatch", formValues, handleInputChange)}
                    {renderInputField("harbourArrival", "intrastat_manual_document_harbour_arrival", formValues, handleInputChange)}
                    {renderInputField("statisticalProcedure", "intrastat_manual_document_statistical_procedure", formValues, handleInputChange)}

                    <s-row>
                        <s-col span="auto" data-testid="countryOfOrigin">
                            <label id="countryOfOriginLabel" htmlFor="countryOfOrigin" className="required">
                                <LanguageStringResolver string="intrastat_manual_document_country_of_origin"/>
                            </label>
                            <SSelect
                                name="countryOfOrigin"
                                aria-labelledby="countryOfOriginLabel"
                                inputId="countryOfOrigin"
                                data-testid="countryOfOrigin-select"
                                optionsList={getCountryDropDownOptions(formValues.countryOfOrigin)}
                                noSearch={false}
                                selectionOptional={true}
                                replacealloptions={true}
                                value={formValues.countryOfOrigin}
                                onS-select={e => handleSelectInputChange(e.detail)}
                                onS-deselect={e => handleDeSelectChange(e.detail)}
                            />
                        </s-col>
                    </s-row>
                    <s-row>
                        <s-col span="auto" data-testid="transactionType">
                            <label htmlFor="transactionType" >
                                <LanguageStringResolver string="intrastat_manual_document_transaction_type"/>
                            </label>
                            <SSelect
                                name="transactionType"
                                data-testid="transactionType-select"
                                value={formValues.transactionType}
                                inputId="transactionType"
                                optionsList={mapToAddKeyValues(props.transactionType, formValues.transactionType, false)}
                                noSearch={false}
                                selectionOptional={true}
                                replacealloptions={true}
                                onS-select={e => handleSelectInputChange(e.detail)}
                                onS-deselect={e => handleDeSelectChange(e.detail)}
                            />
                        </s-col>
                    </s-row>
                </form>
                <button className="primary mt15" data-testid="add-button" onClick={onFormSubmit}>
                    <LanguageStringResolver string='intrastat_manual_document_add_button'/></button>
            </s-container>
        </>
    );
};

IntrastatForm.defaultProps = {
    selectedValues: { country: ""},
    countryList:[],
    modeOfTransport:[],
    deliveryConditions:[],
    transactionType:[]
};
export default IntrastatForm;